import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, min, value } = this.getCurrentValues();
    const remainders = [];

    if (max !== null) {
      remainders.push(max);
    }

    const productLimit = parseIntOrDefault(this.dataset.productLimit, null);
    if (productLimit !== null) {
      const otherQty = Math.max(this.#sumProductCartQuantity() - value, 0);
      remainders.push(Math.max(productLimit - otherQty, min));
    }

    if (remainders.length === 0) return null;
    return Math.min(...remainders);
  }

  /**
   * Sums the current input values of cart lines for the same product.
   * @returns {number}
   */
  #sumProductCartQuantity() {
    const fallback = parseIntOrDefault(this.refs.quantityInput.getAttribute('data-product-cart-quantity'), 0);
    const productId = this.dataset.productId;
    if (!productId) return fallback;

    const root = this.closest('cart-items-component');
    if (!root) return fallback;

    const selectors = root.querySelectorAll(`cart-quantity-selector-component[data-product-id="${productId}"]`);
    if (selectors.length === 0) return fallback;

    let sum = 0;
    for (const selector of selectors) {
      const input = selector.querySelector('input');
      sum += parseIntOrDefault(input?.value, 0);
    }
    return sum;
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const hasProductLimit = parseIntOrDefault(this.dataset.productLimit, null) !== null;

    minusButton.disabled = value <= min;
    plusButton.disabled = !hasProductLimit && effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
